import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	fetchNotificationInbox,
	fetchUnreadCount,
	markAllNotificationsRead,
	markNotificationRead,
} from '../../actions/notificationInbox';
import { getCurrentDepartment } from '../../actions/apiGetInfo';
import {
	setCurrentDealership,
	setCurrentDepartment,
	setVehicleFilter,
} from '../../actions/apiSetInfo';
import { change_page } from '../../actions/toggleMenu';
import { badgeLabel, partialVin, relativeTime, statusParts } from '../../utils/notificationInboxDisplay';
import bellIcon from '../../assets/icons/notifications/bell.svg';
import bellHoverIcon from '../../assets/icons/notifications/bell-hover.svg';
import arrowIcon from '../../assets/icons/notifications/arrow-right.svg';
import './NotificationBell.scss';

const EMPTY = {
	unread: {
		title: 'You’re all caught up',
		body: 'New assignments and updates will appear here as they happen.',
	},
	all: {
		title: 'No notifications yet',
		body: 'When vehicles move through recon, you’ll see updates here.',
	},
};

function StatusLine({ note }) {
	const parts = statusParts(note);
	const time = <span className="notification-time">{relativeTime(note.createdAt)}</span>;
	if (parts.kind === 'transition') {
		return (
			<span className="notification-status-line">
				<span>{parts.from}</span>
				<img className="notification-arrow" src={arrowIcon} alt="" width="14" height="15" />
				<span>{parts.to}</span>
				{time}
			</span>
		);
	}
	if (parts.kind === 'phrase') {
		return (
			<span className="notification-status-line">
				<span>{parts.lead}</span>
				<span>{parts.step}</span>
				{time}
			</span>
		);
	}
	return (
		<span className="notification-status-line">
			<span>{parts.text}</span>
			{time}
		</span>
	);
}

const POLL_MS = 60000;
const SCROLL_MS = 15000;
const NO_ROWS = [];

function badgeClass(badge) {
	const key = String(badge || '').toLowerCase().replace(/\s+/g, '-');
	return key ? `notification-badge notification-badge-${key}` : 'notification-badge';
}

const NotificationBell = () => {
	const dispatch = useDispatch();
	const currentDealership = useSelector(state => state.settings_dealerships.current_dealership);
	const dealerships = useSelector(state => state.settings_dealerships.set_dealerships || NO_ROWS);
	const departments = useSelector(state => state.settings_departments.departments || NO_ROWS);

	const [open, setOpen] = useState(false);
	const [scope, setScope] = useState('unread');
	const [items, setItems] = useState([]);
	const [unreadCount, setUnreadCount] = useState(0);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [pending, setPending] = useState(null);

	const rootRef = useRef(null);
	const scopeRef = useRef(scope);
	const openRef = useRef(open);
	const scrollTimer = useRef(null);
	const requestSeq = useRef(0);
	const panelSeq = useRef(0);
	scopeRef.current = scope;
	openRef.current = open;

	const applyInbox = useCallback((nextScope, inbox) => {
		setUnreadCount(inbox.unreadCount);
		if (scopeRef.current === nextScope) {
			setItems(inbox.notifications);
		}
		setError('');
	}, []);

	// Only the newest request may write state; older responses are dropped so a
	// slow poll cannot undo a later mark-read.
	const load = useCallback(async (nextScope, { panel } = {}) => {
		const seq = ++requestSeq.current;
		const panelId = panel ? ++panelSeq.current : 0;
		if (panel) setLoading(true);
		try {
			const inbox = await fetchNotificationInbox(nextScope);
			if (seq === requestSeq.current) applyInbox(nextScope, inbox);
		} catch (err) {
			if (panel && seq === requestSeq.current && scopeRef.current === nextScope) {
				setError("Couldn't load notifications.");
			}
		} finally {
			// Tracked separately so a superseding poll can't leave the spinner on.
			if (panel && panelId === panelSeq.current) setLoading(false);
		}
	}, [applyInbox]);

	// While the panel is closed only the badge count is needed.
	const refresh = useCallback(async () => {
		if (openRef.current) {
			load(scopeRef.current);
			return;
		}
		const seq = ++requestSeq.current;
		try {
			const count = await fetchUnreadCount();
			if (seq === requestSeq.current && count != null) setUnreadCount(count);
		} catch (err) {
			// keep the last known count
		}
	}, [load]);

	useEffect(() => {
		refresh();
		const timer = setInterval(() => {
			if (typeof document !== 'undefined' && document.hidden) return;
			refresh();
		}, POLL_MS);
		const onFocus = () => refresh();
		window.addEventListener('focus', onFocus);
		return () => {
			clearInterval(timer);
			window.removeEventListener('focus', onFocus);
		};
	}, [refresh, currentDealership]);

	useEffect(() => () => {
		if (scrollTimer.current) clearInterval(scrollTimer.current);
	}, []);

	useEffect(() => {
		if (!open) return undefined;
		const onDown = (event) => {
			if (rootRef.current && !rootRef.current.contains(event.target)) {
				setOpen(false);
			}
		};
		const onKey = (event) => {
			if (event.key === 'Escape') setOpen(false);
		};
		document.addEventListener('mousedown', onDown);
		document.addEventListener('keydown', onKey);
		return () => {
			document.removeEventListener('mousedown', onDown);
			document.removeEventListener('keydown', onKey);
		};
	}, [open]);

	const watchVehicle = (vehicleId) => {
		if (scrollTimer.current) clearInterval(scrollTimer.current);
		const started = Date.now();
		scrollTimer.current = setInterval(() => {
			const selector = `[data-vehicle-id="${CSS.escape(String(vehicleId))}"]`;
			const el = document.querySelector(selector);
			if (el) {
				el.scrollIntoView({ block: 'center' });
				clearInterval(scrollTimer.current);
			} else if (Date.now() - started > SCROLL_MS) {
				clearInterval(scrollTimer.current);
			}
		}, 250);
	};

	useEffect(() => {
		if (!pending) return;
		if (pending.subdomain && currentDealership && pending.subdomain !== currentDealership) return;
		if (pending.waitForDepartments && departments === pending.departmentsSnapshot) return;
		// Empty list means the new store has not loaded yet. Navigating now
		// leaves pending set and the effect runs again on the next array.
		if (departments.length === 0) return;

		const dept = departments.find(item => String(item.id) === String(pending.departmentId));
		dispatch(change_page('vehicles'));
		if (!dept || dept.id == null) {
			setPending(null);
			return;
		}
		dispatch(setCurrentDepartment({ department: dept }));
		dispatch(setVehicleFilter({ type: 'department', value: String(dept.id) }, dept.id));
		dispatch(getCurrentDepartment(dept.id));
		if (pending.vehicleId != null) watchVehicle(pending.vehicleId);
		setPending(null);
	}, [pending, departments, currentDealership, dispatch]);

	// Give up on a navigation that never finds its departments.
	useEffect(() => {
		if (!pending) return undefined;
		const timer = setTimeout(() => setPending(null), SCROLL_MS);
		return () => clearTimeout(timer);
	}, [pending]);

	const openTarget = (note) => {
		const subdomain = note.subdomain || '';
		const sameStore = !subdomain || subdomain === currentDealership;
		dispatch(change_page('vehicles'));
		if (!sameStore) {
			const match = dealerships.find(item => item.subdomain === subdomain);
			const database = (match && match.database) || note.database;
			const dealershipId = (match && match.id) || note.dealershipId;
			if (database && subdomain && dealershipId) {
				setPending({
					vehicleId: note.vehicleId,
					departmentId: note.departmentId,
					subdomain,
					waitForDepartments: true,
					departmentsSnapshot: departments,
				});
				dispatch(setCurrentDealership(database, subdomain, dealershipId));
				return;
			}
		}
		setPending({
			vehicleId: note.vehicleId,
			departmentId: note.departmentId,
			subdomain: subdomain || currentDealership,
			waitForDepartments: false,
			departmentsSnapshot: null,
		});
	};

	const toggle = () => {
		const next = !open;
		setOpen(next);
		if (next) load(scopeRef.current, { panel: true });
	};

	const selectScope = (next) => {
		setScope(next);
		load(next, { panel: true });
	};

	const markOne = async (note) => {
		if (note.read) return;
		requestSeq.current += 1;
		setItems(prev => prev.map(item => (item.id === note.id ? { ...item, read: true } : item)));
		setUnreadCount(count => Math.max(0, count - 1));
		try {
			await markNotificationRead(note.id);
			await load(scopeRef.current, { panel: openRef.current });
		} catch (err) {
			load(scopeRef.current, { panel: openRef.current });
		}
	};

	const markAll = async () => {
		requestSeq.current += 1;
		setItems(prev => prev.map(item => ({ ...item, read: true })));
		setUnreadCount(0);
		try {
			await markAllNotificationsRead();
			await load(scopeRef.current, { panel: true });
		} catch (err) {
			load(scopeRef.current, { panel: true });
		}
	};

	const openNote = (note) => {
		setOpen(false);
		if (!note.read) {
			requestSeq.current += 1;
			setItems(prev => prev.map(item => (item.id === note.id ? { ...item, read: true } : item)));
			setUnreadCount(count => Math.max(0, count - 1));
			markNotificationRead(note.id).catch(() => load('unread'));
		}
		openTarget(note);
	};

	const countLabel = unreadCount === 1 ? '1 unread' : `${unreadCount} unread`;
	const visibleItems = scope === 'unread' ? items.filter(note => !note.read) : items;
	const emptyCopy = EMPTY[scope] || EMPTY.all;
	const showEmpty = !loading && !error && visibleItems.length === 0;

	return (
		<li className="nav-item headericon notification-bell-item" ref={rootRef}>
			<button
				type="button"
				className={`notification-bell${open ? ' is-open' : ''}`}
				aria-label={unreadCount > 0 ? `Notifications, ${countLabel}` : 'Notifications'}
				aria-expanded={open}
				aria-haspopup="dialog"
				onClick={toggle}
			>
				<img className="notification-bell-icon" src={bellIcon} alt="" width="20" height="20" />
				<img className="notification-bell-icon-hover" src={bellHoverIcon} alt="" width="20" height="20" />
				{unreadCount > 0 ? (
					<span className="notification-count">{unreadCount > 99 ? '99+' : unreadCount}</span>
				) : null}
			</button>
			{open ? (
				<div className="notification-panel" role="dialog" aria-label="Notifications">
					<div className="notification-panel-title">
						<span>Notifications</span>
						{unreadCount > 0 ? (
							<button type="button" className="notification-mark-all" onClick={markAll}>
								Mark all read
							</button>
						) : null}
					</div>
					<div className="notification-tabs" role="tablist">
						<button
							type="button"
							role="tab"
							aria-selected={scope === 'unread'}
							className={scope === 'unread' ? 'is-active' : ''}
							onClick={() => selectScope('unread')}
						>
							{`Unread (${unreadCount})`}
						</button>
						<button
							type="button"
							role="tab"
							aria-selected={scope === 'all'}
							className={scope === 'all' ? 'is-active' : ''}
							onClick={() => selectScope('all')}
						>
							All
						</button>
					</div>
					<div className="notification-panel-body">
						{loading ? <div className="notification-loading">Loading…</div> : null}
						{error ? <div className="notification-error">{error}</div> : null}
						{showEmpty ? (
							<div className="notification-empty">
								<p className="notification-empty-title">{emptyCopy.title}</p>
								<p className="notification-empty-body">{emptyCopy.body}</p>
							</div>
						) : null}
						{!error && visibleItems.map(note => {
							const vin = partialVin(note.vin);
							const ymm = [note.year, note.make, note.model].filter(Boolean).join(' ');
							return (
								<div className={`notification-row-wrap${note.read ? ' is-read' : ''}`} key={note.id}>
									<button
										type="button"
										className="notification-row"
										data-notification-id={note.id}
										onClick={() => openNote(note)}
									>
										{note.badge ? <span className={badgeClass(note.badge)}>{badgeLabel(note.badge)}</span> : <span className="notification-badge" />}
										<span className="notification-row-main">
											{note.storeName ? <span className="notification-store">{note.storeName}</span> : null}
											<span className="notification-vehicle">
												{ymm ? <span className="notification-ymm">{ymm}</span> : null}
												{note.stock ? <span className="notification-stock">{note.stock}</span> : null}
												{vin ? <span className="notification-vin">{`(...${vin})`}</span> : null}
											</span>
											<StatusLine note={note} />
										</span>
									</button>
									{!note.read ? (
										<button
											type="button"
											className="notification-mark"
											onClick={() => markOne(note)}
										>
											Mark read
										</button>
									) : null}
								</div>
							);
						})}
					</div>
				</div>
			) : null}
		</li>
	);
};

export default NotificationBell;
